import React, { useCallback, useEffect, useRef, useState } from "react";
import ExportPdfPage from "../ExportPdfPage";
import { exportSelectedSheetsToPdf, listWorkbookSheets } from "../../excel/exportBoqPdf";
import { openPdfDialog } from "../../pdfExport/pdfDialogBridge";

/**
 * Route #export-pdf (the ribbon button): opens the PDF export window and keeps the Excel work
 * (reading sheets, generating the file) here in the taskpane. Without dialog support (e.g. a
 * browser) the old in-pane page is shown instead.
 */
export default function PdfExportLauncher({ onBack, projects = [], customers = [] }) {
  const [state, setState] = useState("opening"); // opening | open | closed | fallback
  const dialogRef = useRef(null);
  const dataRef = useRef({ projects, customers });
  dataRef.current = { projects, customers };

  // Everything the window needs from Excel and from the open project.
  const buildInit = useCallback(async () => {
    const { activeName, allNames } = await listWorkbookSheets();
    let activeId = "";
    let activeRoot = "";
    try {
      activeId = window.localStorage.getItem("boq.activeProjectId") || "";
      activeRoot = window.localStorage.getItem("boq.activeRootSheet") || "";
    } catch { /* ignore */ }
    const project = dataRef.current.projects.find((p) => String(p.id) === String(activeId)) || null;
    const customer = project ? dataRef.current.customers.find((c) => String(c.id) === String(project.customer_id)) : null;
    const day = new Date().toISOString().split("T")[0];
    return {
      activeName,
      activeRoot,
      allNames,
      cover: {
        projectName: project?.name || "",
        customer: customer ? (customer.inn ? `${customer.name} / ${customer.inn}` : customer.name) : "",
        location: project?.location || "",
        area: project?.area ? String(project.area) : "",
        docNumber: project?.doc_number || "",
        date: project?.date || "",
        author: "",
      },
      defaultFileName: project?.name ? `BOQ_${project.name}_${day}`.replace(/\s+/g, "_") : `BOQ_${day}`,
    };
  }, []);

  const open = useCallback(async () => {
    setState("opening");
    const handle = await openPdfDialog({
      buildInit,
      runExport: exportSelectedSheetsToPdf,
      onClosed: () => {
        dialogRef.current = null;
        setState("closed");
      },
    });
    if (!handle) {
      setState("fallback");
      return;
    }
    dialogRef.current = handle;
    setState("open");
  }, [buildInit]);

  useEffect(() => {
    open();
    return () => dialogRef.current?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === "fallback") return <ExportPdfPage onBack={onBack} projects={projects} customers={customers} />;

  return (
    <div data-no-translate="true" style={{ padding: "8px 4px" }}>
      <button className="link" onClick={onBack}>← უკან</button>
      <h2 style={{ marginTop: 16, marginBottom: 6 }}>PDF ექსპორტი</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 16 }}>
        {state === "opening" && "ექსპორტის ფანჯარა იხსნება…"}
        {state === "open" && "ექსპორტის ფანჯარა გახსნილია. აირჩიეთ ფურცლები და პარამეტრები იქ. ექსპორტის დროს ეს პანელი გახსნილი დატოვეთ."}
        {state === "closed" && "ექსპორტის ფანჯარა დაიხურა."}
      </p>
      {state !== "opening" && (
        <button type="button" className="primary" style={{ width: "100%", padding: 10, borderRadius: 8 }} onClick={open}>
          {state === "open" ? "ფანჯრის თავიდან გახსნა" : "ექსპორტის ფანჯრის გახსნა"}
        </button>
      )}
    </div>
  );
}
